import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";
// util
import { useEventFlexibility } from "#webapp/src/hooks";
import { isSmartHold } from "#webapp/src/util/event.util";
import { EFlex } from "@clockwise/client-commons/src/util/event-tag";
import {
  getAbsoluteDate,
  getAbsoluteDateTime,
} from "@clockwise/client-commons/src/util/format-time";
import { fontFamilyBody, greys } from "@clockwise/web-commons/src/styles";
import { ZonedMoment } from "@clockwise/web-commons/src/util/time-zone.util";
import { makeStyles } from "@material-ui/core";
import getEventRecurrenceDescription from "./util/getEventRecurrenceDescription";

interface IContainer {
  org: ISchema.IOrg;
  event: ISchema.IEvent;
  startTime: ZonedMoment;
  endTime: ZonedMoment;
  unemphasized?: boolean;
  isAllDay?: boolean;
  selectedFlex?: EFlex;
  calendarId?: string;
}

const useStyles = makeStyles({
  wrapper: {
    marginBottom: 16,
  },
  body: {
    fontFamily: fontFamilyBody,
    fontSize: `${14 / 16}rem`,
    color: greys.darkGreyBlue,
    lineHeight: `20px`,
  },
});

export const EventTimeAndRecurrence = ({
  event,
  startTime,
  endTime,
  isAllDay,
  calendarId,
  org,
}: IContainer) => {
  const classes = useStyles();

  const eventFlexibility = useEventFlexibility({
    calendarId: String(calendarId),
    orgId: org.id,
    externalEventId: event.eventKey?.externalEventId,
  });

  const recurrence = getEventRecurrenceDescription(event, eventFlexibility);

  /**
   * All day events have an end time of midnight on the next day, so we back it up to the previous day.
   * i.e. An all day event for Nov 1st will have an end time of midnight on Nov 2nd.
   */
  const endTimeAdjusted = endTime.clone().subtract(1, "minutes");
  const isSingleDay = startTime.isSame(endTimeAdjusted, "days");

  const formattedDate = isAllDay
    ? getAbsoluteDate(startTime, isSingleDay ? undefined : endTimeAdjusted, true)
    : getAbsoluteDateTime(startTime, endTime);

  return (
    <div className={classes.wrapper}>
      <p className={classes.body}>{formattedDate}</p>
      {event.recurrenceRules && !isSmartHold(event) && <p className={classes.body}>{recurrence}</p>}
    </div>
  );
};
